// Onboarding funnel data layer. Reads `/v1/onboarding/state` and drives
// `/v1/onboarding/advance` — the route component renders whatever `state`
// comes back. The state machine itself lives in `@developerz/domain`; here we
// just round-trip it over `apiFetch` (reusing the dev session bearer).

import type { NextFunnelMove, OnboardingEvent } from '@developerz/domain';
import { OnboardingState } from '@developerz/domain';
import { createQuery } from '@tanstack/solid-query';
import { activeAccountId } from './active-account';
import { apiFetch } from './api';
import { SESSION_TOKEN } from './session';

/**
 * `GET /v1/onboarding/state` — minted by `onboardingView` in
 * `apps/api/src/deps/onboarding.ts`, which owns the field list.
 *
 * `next_move` is the server's `nextFunnelMove(state)` and is DELIBERATELY not
 * what the wizard's buttons fire. It cannot be: at five of the funnel's eleven
 * states its `kind` is `platform_witnessed` — a provider accepting a key, the
 * App install webhook, a box's first heartbeat, a chat client's live token, the
 * policy ingest's verdict — and no control on this screen may assert any of
 * them, because the subject of an observation is not its witness
 * (`EVENT_WITNESS` in `@developerz/domain`).
 *
 * It REPLACED `next_event: OnboardingEvent | null` (arc 101 slice 05, #4611),
 * and the rename is why this file is honest today: the old name survived the
 * change here for a while, describing a field the server had stopped sending.
 * The previous sentence in this docblock claimed `next_event` was `null` at
 * `yml_requested`; it never was — `yml_requested` waits on `mcp_connected`, and
 * `null` was only ever `active`'s. That is the failure a restated wire shape
 * produces, and `src/testing/onboarding-fixture.ts` is the answer to it.
 *
 * `lib/onboardingSteps.ts` holds what each card offers, and
 * `onboardingSteps.test.ts` pins every advance it declares against
 * `nextFunnelMove` and `advance` from the SAME domain module the server calls —
 * so the two are one transition table checked mechanically, not two copies.
 */
export type OnboardingStatus = {
  account_id: string;
  state: OnboardingState;
  next_move: NextFunnelMove;
  complete: boolean;
  /** `null`: the funnel is finished or the read failed, and the card goes by `state`. */
  facts: OnboardingJourneyFacts | null;
};

export type OnboardingJourneyFacts = {
  github: boolean;
  llm_key: boolean;
  mcp: boolean;
  repos: boolean;
  box: boolean;
};

/**
 * The step a person is on (#7802): the FIRST thing in their order they have not done. Sign up,
 * connect GitHub, LLM, MCP, then where and how, and a server after that, since a pass needs one.
 * The stored `state` keeps the ladder's older order and catches up on these same facts, so this
 * reads no history: a state names a card, and which card is decided here.
 *
 * Pre-GitHub states are stored steps a fact cannot answer (the sign-in, the plan pick), and past
 * the box every fact is true, so the stored state's card (setup PR, validate) is the honest one.
 */
export function displayedOnboardingState(status: OnboardingStatus): OnboardingState {
  const stored = status.state;
  const { facts } = status;
  if (!facts || stored === OnboardingState.New || stored === OnboardingState.Authed) return stored;
  if (stored === OnboardingState.Invalid || stored === OnboardingState.Active) return stored;
  if (!facts.github) return OnboardingState.KeySet;
  if (!facts.llm_key) return OnboardingState.PlanSelected;
  if (!facts.mcp) return OnboardingState.YmlRequested;
  if (!facts.repos) return OnboardingState.BoxAttached;
  if (!facts.box) return OnboardingState.GhConnected;
  return stored;
}

export const ONBOARDING_QUERY_KEY = 'onboarding';

/**
 * The onboarding plan pick (arc 101 slice 02): `single` → `solo`, `team` → `pro`.
 * Carried only with the `plan_selected` advance; the API records it as billing
 * *intent* (a soft hint that pre-selects the checkout tier), never the billing
 * truth (`tier`/`seats`, set by the Stripe subscription). Matches the API's
 * `intended_tier` enum (`apps/api/src/routes/onboarding.ts`).
 */
export type IntendedTier = 'solo' | 'pro';

// ponytail: env-config'd with a sensible default. Real value comes from the
// GitHub App slug at deploy time. Used by the wizard's connect step and the
// install empty-state CTAs.
export const GH_APP_INSTALL_URL =
  import.meta.env['PUBLIC_GH_APP_INSTALL_URL'] ??
  'https://github.com/apps/developerz-ai/installations/new';

/**
 * Progress-rail step labels (i18n keys) for the server-driven funnel — one per
 * `OnboardingState`, in order. Paid activation is NOT here: it isn't a funnel
 * state (see {@link onboardingStepLabels}).
 *
 * Order = the domain funnel (`NEW → AUTHED → PLAN_SELECTED → KEY_SET →
 * GH_CONNECTED → BOX_ATTACHED → REPOS_SELECTED → YML_REQUESTED → MCP_CONNECTED →
 * ACTIVE`). A label names the step the user is DOING at that index, and a state
 * names the fact already settled, so the two are offset by one: `gh_connected`
 * sits on "Add a server", `box_attached` on "Select repos". Epic #3629 added
 * the two platform-witnessed steps (a box that beat, a chat client that
 * connected) — the funnel used to end with no machine to run on. Keep this list
 * index-aligned with {@link STATE_STEP}.
 */
export const STEP_LABELS = [
  'onboarding.step_auth',
  'onboarding.step_plan',
  'onboarding.step_connect',
  'onboarding.step_keys',
  'onboarding.step_mcp',
  'onboarding.step_repos',
  'onboarding.step_box',
  'onboarding.step_yml',
  'onboarding.step_validate',
] as const;

/**
 * The CARD a state names → its progress-rail index, in the person's order (#7802): the rail follows
 * {@link displayedOnboardingState}, not the stored ladder. `Invalid` sits on the
 * validate step; `Active` is past the end of {@link STEP_LABELS} — which is
 * exactly the index the paid-seat step takes when it's present, so a
 * finished-but-unpaid account lands ON it rather than reading as all-done (see
 * {@link onboardingStepLabels}).
 *
 * The rail paints every index below `current` as done, so a legal funnel
 * transition that LOWERS an index un-paints finished work. `onboarding.test.tsx`
 * § "the progress rail never walks backwards" asserts that over every edge
 * `advance` admits and pins the ONE that does it today (`invalid --yml_retried->
 * yml_requested`, where the domain deliberately takes the weaker state). A
 * second backwards edge is red there, not in a screenshot.
 */
export const STATE_STEP: Record<OnboardingState, number> = {
  [OnboardingState.New]: 0,
  [OnboardingState.Authed]: 1,
  [OnboardingState.KeySet]: 2,
  [OnboardingState.PlanSelected]: 3,
  [OnboardingState.YmlRequested]: 4,
  [OnboardingState.BoxAttached]: 5,
  [OnboardingState.GhConnected]: 6,
  [OnboardingState.ReposSelected]: 7,
  [OnboardingState.McpConnected]: 8,
  [OnboardingState.Invalid]: 8,
  [OnboardingState.Active]: STEP_LABELS.length,
};

/** i18n key of the paid-seat activation step (arc 101 slice 07). */
export const SEAT_STEP_LABEL = 'onboarding.step_seats';

/**
 * The rail this caller should see: the nine install/config steps, plus a tenth
 * **paid activation** step while a seat plan is still to be bought. Pass
 * `useSeatOffer()` — the same gate `OnboardingBuySeats` renders behind, so the
 * rail can never promise a step whose card isn't there (or hide one that is).
 *
 * Appended, not spliced in after the install step, for two reasons:
 *  - the rail paints every index below `current` as done, so a step sitting
 *    before it is claimed complete — and seats can be bought at any point, or
 *    never. A mid-rail seat step would read "done" the moment the funnel moved
 *    past it, which is a lie about money;
 *  - the checkout cart lives on the terminal screen, so the last index is the
 *    one the user is actually standing on when that card appears.
 *
 * Deliberately DERIVED, not an `OnboardingState` (none of the funnel's states is
 * billing-related — and this is why): the funnel gates activation, while an
 * account is fully usable unpaid, so a funnel state would strand every free
 * account on a pay wall. Entitlements are the server's truth for seats; the rail
 * only reflects them.
 */
export function onboardingStepLabels(seatsPending: boolean): readonly string[] {
  return seatsPending ? [...STEP_LABELS, SEAT_STEP_LABEL] : STEP_LABELS;
}

/** States parked waiting on an out-of-band transition — poll so the UI advances
 * on its own once the server moves. `plan_selected` waits for an org key to be
 * verified (which happens on the keys page, not here — see `verifyKey`);
 * `key_set` waits for the GitHub App install webhook; `gh_connected` waits for
 * a box's first heartbeat and `yml_requested` for a chat client's connection
 * (both settled by the API's fact ladder, never asserted here); `mcp_connected`
 * waits for policy validation. */
const POLLED_STATES: readonly OnboardingState[] = [
  OnboardingState.PlanSelected,
  OnboardingState.KeySet,
  OnboardingState.GhConnected,
  OnboardingState.YmlRequested,
  OnboardingState.McpConnected,
];

/**
 * Current funnel state. Disabled (idle) when there's no session, like `useMe`.
 * Polls while parked on a step whose advance comes from elsewhere (see
 * `POLLED_STATES`) so the wizard reflects the transition without a manual reload.
 */
export function useOnboardingState(token: string | null = SESSION_TOKEN) {
  return createQuery<OnboardingStatus>(() => ({
    queryKey: [ONBOARDING_QUERY_KEY, token, activeAccountId()],
    enabled: token !== null,
    queryFn: () => apiFetch<OnboardingStatus>('/v1/onboarding/state', token),
    refetchInterval: (q) => {
      const s = q.state.data?.state;
      return s !== undefined && POLLED_STATES.includes(s) ? 5000 : false;
    },
  }));
}

/**
 * Fire a transition. Returns the new state on success; throws `ApiError` on a
 * 409 invalid-transition (the caller refetches `/state` and re-renders from the
 * server's truth rather than guessing).
 *
 * `intendedTier` rides with the `plan_selected` step only (single/team pick); the
 * API ignores it on every other event and drops it entirely when absent.
 */
export async function advanceOnboarding(
  event: OnboardingEvent,
  token: string | null = SESSION_TOKEN,
  intendedTier?: IntendedTier,
): Promise<OnboardingStatus> {
  return apiFetch<OnboardingStatus>('/v1/onboarding/advance', token, {
    method: 'POST',
    body: JSON.stringify(
      intendedTier === undefined ? { event } : { event, intended_tier: intendedTier },
    ),
  });
}
